// ADR-0154 — el análisis de cartera con IA (el «botón mágico» de Mario).
// El core-api calcula los hallazgos y la IA sólo los redacta: toda cifra
// que se ve en una ficha, su gráfico y su enlace salen del expediente
// (`dossier`), nunca del texto del memo. El memo cita cada afirmación
// con `[[Hk]]`; acá esas citas se vuelven botones que llevan a la ficha.
//
// Los contratistas llegan con su seudónimo («Contratista A»): el
// expediente que se guarda y que lee la IA no lleva nombres. core-api
// resuelve el nombre al leer (`analisis.contratistas`) y acá se cambia en
// el memo y en las fichas; el que no se resuelve (contratista borrado)
// queda con su seudónimo.
//
// Un informe por alcance: la cartera completa (sin `assetGroupId`) o un
// proyecto comparado contra el resto. Generar es explícito porque cuesta
// y cuenta contra la cuota de la organización.

import { Fragment, useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import {
  escalaTramo,
  etiquetaTramo,
  HALLAZGO_CONFIDENCE_LABEL,
  HALLAZGO_KIND_LABEL,
  hallazgoConNombres,
  MEMO_TEMA_LABEL,
  memoConNombres,
  type Hallazgo,
  type PostventaDossier,
} from "@bitacorapro/api-types";
import { ApiError } from "../lib/api/error";
import { mensajeDeError } from "../lib/api/error-message";
import { useGenerarPostventaAnalisis, usePostventaAnalisis } from "../lib/api/queries";
import { useQueryView } from "../lib/loading/useQueryView";
import { RefreshNotices, SlowNotice } from "../routes/queryViewKit";
import { fmtDateTime } from "../lib/util";
import { EmptyState } from "./EmptyState";
import { Icon } from "./icons";
import { PageError } from "./PageError";
import { PageSkeleton } from "./PageSkeleton";
import { Skel } from "./Skel";

const CONFIDENCE_PILL: Record<Hallazgo["confidence"], string> = {
  pista: "st-pill--idle",
  patron_temprano: "st-pill--info",
  patron_confirmado: "st-pill--brand",
};

const num = new Intl.NumberFormat("es-CL", { maximumFractionDigits: 1 });
const fmt = (n: number) => num.format(n);
/** La cifra de una ficha: el tramo completo si la unidad es de un tramo
 *  (encuestas, ADR-0170: el expediente sólo trae su borde inferior), el
 *  número si no. */
const cifra = (unit: string, n: number) => etiquetaTramo(unit, n) ?? fmt(n);

const CITA_RE = /\[\[(H\d+)\]\]/g;

type Props = {
  /** Sin él, la cartera completa; con él, ese proyecto contra el resto. */
  assetGroupId?: string;
  eyebrow: ReactNode;
  title: string;
};

export function AnalisisCartera({ assetGroupId, eyebrow, title }: Props) {
  const query = usePostventaAnalisis(assetGroupId);
  const { data, refetch } = query;
  // ADR-0166: carga / error / listo por el contrato común. Una revisión
  // que falla sobre un informe ya cargado lo conserva y avisa en línea.
  const view = useQueryView(query);
  const generar = useGenerarPostventaAnalisis(assetGroupId);
  const [fichaActiva, setFichaActiva] = useState<string | null>(null);

  const analisis = data?.analisis ?? null;
  const cuota = data?.cuota;
  const agotada = cuota !== undefined && cuota.usados >= cuota.limite;
  const pendiente = generar.isPending;

  function irAFicha(key: string) {
    setFichaActiva(key);
    const el = document.getElementById(`hallazgo-${key}`);
    el?.scrollIntoView?.({ behavior: "smooth", block: "center" });
    el?.focus({ preventScroll: true });
  }

  const boton = (
    <button
      type="button"
      className="btn btn--primary btn--sm"
      onClick={() => generar.mutate()}
      disabled={pendiente || agotada || !data}
    >
      <Icon.Sparkles s={14} />
      {pendiente ? "Analizando…" : analisis ? "Regenerar" : "Generar análisis"}
    </button>
  );

  const header = (
    <div className="page-header">
      <div className="page-header__lead">
        <div className="page-header__eyebrow">{eyebrow}</div>
        <h1 className="page-header__title">{title}</h1>
        <p className="page-header__sub">
          El sistema calcula los hallazgos y la IA los redacta. Cada cifra sale del cálculo y se
          puede abrir.
        </p>
      </div>
      {data && (
        <div className="page-header__actions analisis-acciones">
          {data.sinCambios && analisis && (
            // «Regenerar» sin cambios en las conclusiones devuelve el informe
            // guardado sin llamar al modelo ni gastar un análisis de la cuota.
            <span className="analisis-cuota" role="status">
              {data.cifrasDesactualizadas
                ? `Las conclusiones no cambiaron, pero algunas cifras pueden haber variado: las que ves son del análisis del ${fmtDateTime(analisis.createdAt)}`
                : `Las conclusiones no cambiaron: se mantiene el análisis del ${fmtDateTime(analisis.createdAt)}`}
            </span>
          )}
          {cuota && (
            <span className="analisis-cuota">
              {agotada
                ? "Usaste los análisis de las últimas 24 horas"
                : `${cuota.limite - cuota.usados} de ${cuota.limite} disponibles en 24 horas`}
            </span>
          )}
          {boton}
        </div>
      )}
    </div>
  );

  if (view.kind === "error") {
    const bloqueado = view.error instanceof ApiError && view.error.status === 403;
    return (
      <div className="page">
        {header}
        <div className="panel">
          {bloqueado ? (
            <EmptyState
              title="El análisis no está disponible para tu cuenta"
              description="Lo ven los roles de gestión de una inmobiliaria con postventa."
            />
          ) : view.offline ? (
            <PageError
              title="Sin conexión"
              description="Esta información aún no está guardada en este dispositivo. Conéctate para verla."
              onRetry={view.retry}
            />
          ) : (
            <PageError
              title="No pudimos cargar el análisis"
              description="Revisa tu conexión e intenta de nuevo. Si el problema sigue, avísanos."
              onRetry={view.retry}
            />
          )}
        </div>
      </div>
    );
  }

  if (view.kind === "loading") {
    return (
      <div className="page">
        {header}
        <PageSkeleton label="Cargando el análisis…" visible={view.visible}>
          <div className="panel panel__body skel-row" aria-hidden="true">
            <Skel width="60%" height={20} />
            <Skel width="100%" height={12} />
            <Skel width="90%" height={12} />
            <Skel width="70%" height={12} />
          </div>
        </PageSkeleton>
        {view.slow && <SlowNotice onRetry={() => void refetch()} />}
      </div>
    );
  }

  // La query siempre está habilitada: «disabled» no ocurre en la práctica,
  // pero nunca deja un skeleton eterno; queda sólo el encabezado.
  if (view.kind !== "ready") return <div className="page">{header}</div>;

  let cuerpo: ReactNode;
  if (pendiente) {
    // Los totales del último expediente guardado: el nuevo sólo existe
    // cuando vuelve el POST. Sin informe previo, los pasos van sin cifras.
    cuerpo = <Analizando totals={analisis?.dossier.totals ?? null} />;
  } else if (!analisis) {
    cuerpo = (
      <div className="panel">
        <EmptyState
          title="Aún no hay un análisis"
          description={
            assetGroupId
              ? "Compara este proyecto con el resto de la cartera: observaciones por unidad a igual edad, contratistas y reclamos que se podían evitar."
              : "Una lectura ejecutiva de la cartera: qué proyecto se aparta, qué contratista concentra reaperturas y qué reclamos se podían evitar."
          }
          action={boton}
        />
      </div>
    );
  } else if (!analisis.memo) {
    cuerpo = (
      <div className="panel">
        <EmptyState
          title="No encontramos patrones con evidencia suficiente"
          description="Con los datos de hoy ninguna comparación supera los mínimos. No se usó la IA."
        />
        <Notas notes={analisis.dossier.notes} />
      </div>
    );
  } else {
    const { dossier } = analisis;
    const memo = memoConNombres(analisis.memo, analisis.contratistas);
    const hallazgos = dossier.hallazgos.map((h) => hallazgoConNombres(h, analisis.contratistas));
    cuerpo = (
      <div className="analisis">
        <article className="panel analisis-memo" aria-labelledby="analisis-titular">
          <div className="analisis-memo__meta">
            Generado el {fmtDateTime(analisis.createdAt)} · {resumenTotales(dossier.totals)}
          </div>
          <h2 id="analisis-titular" className="analisis-memo__titular">
            {memo.titular}
          </h2>
          {memo.secciones.map((s, i) => (
            <section key={i} className="analisis-seccion">
              <div className="analisis-seccion__tipo">{MEMO_TEMA_LABEL[s.tipo]}</div>
              <h3 className="analisis-seccion__titulo">{s.titulo}</h3>
              <p className="analisis-seccion__texto">
                <ConCitas texto={s.texto} onCita={irAFicha} />
              </p>
              {s.recomendacion && (
                <div className="analisis-reco">
                  <div className="analisis-reco__label">Recomendación</div>
                  <p>{s.recomendacion}</p>
                  {s.responsable && (
                    <div className="analisis-reco__resp">Responsable: {s.responsable}</div>
                  )}
                </div>
              )}
            </section>
          ))}
          {memo.lecciones.length > 0 && (
            <section className="analisis-seccion">
              <h3 className="analisis-seccion__titulo">Para el próximo proyecto</h3>
              <ul className="analisis-lecciones">
                {memo.lecciones.map((l, i) => (
                  <li key={i}>
                    <ConCitas texto={l.texto} onCita={irAFicha} />
                  </li>
                ))}
              </ul>
            </section>
          )}
          <Notas notes={dossier.notes} />
        </article>

        <aside className="analisis-evidencia" aria-label="Evidencia">
          <div className="analisis-evidencia__head">
            Evidencia · {dossier.hallazgos.length}{" "}
            {dossier.hallazgos.length === 1 ? "hallazgo" : "hallazgos"}
          </div>
          {hallazgos.map((h) => (
            <HallazgoFicha key={h.key} h={h} activa={fichaActiva === h.key} />
          ))}
        </aside>
      </div>
    );
  }

  return (
    <div className="page">
      {header}
      <RefreshNotices
        refreshFailed={view.refreshFailed}
        revalidatingHint={view.revalidatingHint}
        onRetry={() => void refetch()}
      />
      {generar.error && !pendiente && (
        <p className="notice-banner analisis-error" role="alert">
          {mensajeDeError(
            generar.error,
            "La IA no respondió a tiempo. Intenta de nuevo en unos minutos."
          )}
        </p>
      )}
      {cuerpo}
    </div>
  );
}

function resumenTotales(t: PostventaDossier["totals"]): string {
  const p = (n: number, uno: string, varios: string) => `${fmt(n)} ${n === 1 ? uno : varios}`;
  return `${p(t.observations, "observación", "observaciones")} en ${p(t.units, "unidad", "unidades")} de ${p(t.projects, "proyecto", "proyectos")}`;
}

/** Los pasos reales del cálculo, en orden. El ritmo es de pantalla: el
 *  POST es uno solo y el último paso queda activo hasta que vuelve. */
function Analizando({ totals }: { totals: PostventaDossier["totals"] | null }) {
  const pasos = [
    totals ? `Analizando ${resumenTotales(totals)}` : "Reuniendo las observaciones de la cartera",
    "Calculando hallazgos: proyectos a igual edad, contratistas y reclamos evitables",
    "Redactando el memo y verificando cada cifra",
  ];
  const [paso, setPaso] = useState(0);
  useEffect(() => {
    const t1 = setTimeout(() => setPaso(1), 1500);
    const t2 = setTimeout(() => setPaso(2), 3500);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  return (
    <div className="panel analisis-cargando" aria-busy="true">
      <span className="sr-only" role="status">
        {pasos[paso]}
      </span>
      <div className="analisis-cargando__icono" aria-hidden="true">
        <Icon.Sparkles s={22} />
      </div>
      <ol className="analisis-pasos" aria-hidden="true">
        {pasos.map((texto, i) => (
          <li
            key={i}
            className={
              i < paso ? "analisis-paso--hecho" : i === paso ? "analisis-paso--activo" : undefined
            }
          >
            {i < paso ? <Icon.Check s={14} /> : <span className="analisis-paso__punto" />}
            {texto}
            {i === paso && "…"}
          </li>
        ))}
      </ol>
    </div>
  );
}

/** El texto del memo con cada `[[Hk]]` convertido en un botón a su ficha. */
function ConCitas({ texto, onCita }: { texto: string; onCita: (key: string) => void }) {
  const partes: ReactNode[] = [];
  let desde = 0;
  for (const m of texto.matchAll(CITA_RE)) {
    const key = m[1]!;
    partes.push(texto.slice(desde, m.index));
    partes.push(
      <button
        key={`${key}-${m.index}`}
        type="button"
        className="analisis-cita"
        onClick={() => onCita(key)}
        aria-label={`Ver hallazgo ${key}`}
      >
        {key}
      </button>
    );
    desde = m.index + m[0].length;
  }
  partes.push(texto.slice(desde));
  return (
    <>
      {partes.map((p, i) => (
        <Fragment key={i}>{p}</Fragment>
      ))}
    </>
  );
}

function Notas({ notes }: { notes: string[] }) {
  if (notes.length === 0) return null;
  return (
    <div className="analisis-notas">
      <div className="analisis-notas__label">Lo que no se pudo comparar</div>
      <ul>
        {notes.map((n, i) => (
          <li key={i}>{n}</li>
        ))}
      </ul>
    </div>
  );
}

export function HallazgoFicha({ h, activa }: { h: Hallazgo; activa: boolean }) {
  const { contrast, evidence } = h;
  const muestra = [
    `${fmt(evidence.n)} ${evidence.nUnit}`,
    evidence.projects !== null
      ? `${evidence.projects} ${evidence.projects === 1 ? "proyecto" : "proyectos"}`
      : null,
  ]
    .filter(Boolean)
    .join(" · ");

  return (
    <section
      id={`hallazgo-${h.key}`}
      tabIndex={-1}
      className={activa ? "hallazgo hallazgo--activa" : "hallazgo"}
      aria-labelledby={`hallazgo-${h.key}-titulo`}
    >
      <div className="hallazgo__head">
        <span className="hallazgo__key">{h.key}</span>
        <span className="hallazgo__kind">{HALLAZGO_KIND_LABEL[h.kind]}</span>
        <span className={`st-pill ${CONFIDENCE_PILL[h.confidence]}`}>
          {HALLAZGO_CONFIDENCE_LABEL[h.confidence]}
        </span>
      </div>
      <h3 id={`hallazgo-${h.key}-titulo`} className="hallazgo__headline">
        {h.headline}
      </h3>
      <div className="hallazgo__contraste">
        <span className="hallazgo__valor">{cifra(contrast.unit, contrast.value)}</span>
        <span className="hallazgo__ref">contra {cifra(contrast.unit, contrast.reference)}</span>
      </div>
      <div className="hallazgo__unidad">
        {contrast.unit} · {contrast.referenceLabel}
      </div>
      <HallazgoGrafico h={h} />
      <div className="hallazgo__muestra">Muestra: {muestra}</div>
      {h.caveats.length > 0 && (
        <ul className="hallazgo__caveats">
          {h.caveats.map((c, i) => (
            <li key={i}>
              <Icon.Warning s={13} />
              <span>{c}</span>
            </li>
          ))}
        </ul>
      )}
      {evidence.href && (
        <Link to={evidence.href} className="hallazgo__casos">
          Ver los casos <Icon.ChevronRight s={14} />
        </Link>
      )}
    </section>
  );
}

/** El mini gráfico de la ficha. Proyectos trae una curva por mes desde la
 *  entrega (dos líneas en la misma escala); el resto, una barra por
 *  categoría con la referencia como marca. La fila del sujeto del
 *  hallazgo va en el color de marca y el resto atenuado. */
export function HallazgoGrafico({ h }: { h: Hallazgo }) {
  const { series, seriesLabels } = h.evidence;
  if (series.length === 0) return null;
  const conReferencia = series.some((p) => p.reference !== null);
  const unit = h.contrast.unit;
  const valor = (n: number) => cifra(unit, n);

  const leyenda = (
    <div
      className={
        h.kind === "proyectos" ? "hallazgo-leyenda" : "hallazgo-leyenda hallazgo-leyenda--barras"
      }
      aria-hidden="true"
    >
      <span>
        <i className="hallazgo-leyenda__valor" />
        {seriesLabels.value}
      </span>
      {conReferencia && (
        <span>
          <i className="hallazgo-leyenda__ref" />
          {seriesLabels.reference}
        </span>
      )}
    </div>
  );

  // El envoltorio lleva el `sr-only`: una <table> no se encoge a 1 px y
  // desbordaba la página a lo ancho.
  const tabla = (
    <div className="sr-only">
      <table>
        <caption>{h.question}</caption>
        <thead>
          <tr>
            <th scope="col"></th>
            <th scope="col">{seriesLabels.value}</th>
            {conReferencia && <th scope="col">{seriesLabels.reference}</th>}
          </tr>
        </thead>
        <tbody>
          {series.map((p) => (
            <tr key={p.label}>
              <th scope="row">{p.label}</th>
              <td>{valor(p.value)}</td>
              {conReferencia && <td>{p.reference === null ? "—" : valor(p.reference)}</td>}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const max = Math.max(...series.flatMap((p) => [p.value, p.reference ?? 0]), 0.0001);

  if (h.kind === "proyectos") {
    const W = 280;
    const H = 72;
    const PAD = 4;
    const x = (i: number) => PAD + (i * (W - 2 * PAD)) / Math.max(series.length - 1, 1);
    const y = (v: number) => H - PAD - (v / max) * (H - 2 * PAD);
    const linea = (vals: (number | null)[]) =>
      vals
        .map((v, i) => (v === null ? null : `${i === 0 ? "M" : "L"}${x(i)},${y(v)}`))
        .filter(Boolean)
        .join(" ");
    return (
      <div className="hallazgo-grafico">
        <svg
          className="hallazgo-curva"
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <line className="hallazgo-curva__base" x1={0} x2={W} y1={H - PAD} y2={H - PAD} />
          {conReferencia && (
            <path className="hallazgo-curva__ref" d={linea(series.map((p) => p.reference))} />
          )}
          <path className="hallazgo-curva__valor" d={linea(series.map((p) => p.value))} />
          {series.map((p, i) => (
            <circle key={p.label} className="hallazgo-curva__hit" cx={x(i)} cy={y(p.value)} r={8}>
              <title>
                {`${p.label}: ${fmt(p.value)}${p.reference === null ? "" : ` contra ${fmt(p.reference)}`}`}
              </title>
            </circle>
          ))}
        </svg>
        <div className="hallazgo-curva__eje" aria-hidden="true">
          <span>{series[0]!.label}</span>
          <span>{series[series.length - 1]!.label}</span>
        </div>
        {leyenda}
        {tabla}
      </div>
    );
  }

  // Un tramo de encuesta se dibuja sobre su escala completa (el NPS va de
  // −100 a 100); el resto, desde cero hasta el mayor valor.
  const escala = escalaTramo(unit) ?? { min: 0, max };
  const ancho = (v: number) => ((v - escala.min) / (escala.max - escala.min)) * 100;
  const titular = h.headline.toLocaleLowerCase("es-CL");
  return (
    <div className="hallazgo-grafico">
      <ul
        className={escalaTramo(unit) ? "hallazgo-barras hallazgo-barras--tramo" : "hallazgo-barras"}
        aria-hidden="true"
      >
        {series.map((p) => {
          const sujeto = titular.includes(p.label.toLocaleLowerCase("es-CL"));
          return (
            <li
              key={p.label}
              className={sujeto ? "hallazgo-barra hallazgo-barra--sujeto" : "hallazgo-barra"}
              title={`${p.label}: ${valor(p.value)}${p.reference === null ? "" : ` contra ${valor(p.reference)}`}`}
            >
              <span className="hallazgo-barra__label">{p.label}</span>
              <span className="hallazgo-barra__pista">
                <span className="hallazgo-barra__fill" style={{ width: `${ancho(p.value)}%` }} />
                {p.reference !== null && (
                  <span
                    className="hallazgo-barra__ref"
                    style={{ left: `${ancho(p.reference)}%` }}
                  />
                )}
              </span>
              <span className="hallazgo-barra__valor">{valor(p.value)}</span>
            </li>
          );
        })}
      </ul>
      {leyenda}
      {tabla}
    </div>
  );
}
